// AI settings: generate with Toons AI (monthly credits) or with the user's own Claude / OpenAI key.
// The key is checked with the provider and stored encrypted by the API; it is never shown again
// (only its last 4 characters).

import React, { useEffect, useState } from "react";
import { useAIJobs } from "./AIJobsContext";

const PROVIDERS = {
  claude: { name: "Claude (Anthropic)", short: "Claude", prefix: "sk-ant-", where: "console.anthropic.com", url: "https://console.anthropic.com/settings/keys" },
  openai: { name: "ChatGPT (OpenAI)", short: "OpenAI", prefix: "sk-", where: "platform.openai.com", url: "https://platform.openai.com/api-keys" },
};

function resetDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (e) {
    return "";
  }
}

export function CreditsMeter({ credits }) {
  if (!credits) return null;
  if (credits.unlimited) return <div className="ais-credits"><strong>Unlimited</strong> (admin)</div>;
  const total = credits.monthly + credits.extra;
  const left = credits.remaining;
  const pct = total > 0 ? Math.round((100 * Math.min(left, total)) / total) : 0;
  return (
    <div className="ais-credits">
      <div className="ais-credits-row">
        <span><strong>{left}</strong> of {total} credits left</span>
        <span className="ais-muted">Resets {resetDate(credits.resetsOn)}</span>
      </div>
      <div className="ais-meter"><span style={{ width: pct + "%" }} /></div>
      {credits.extra > 0 && <div className="ais-muted">Includes {credits.extra} extra credits</div>}
    </div>
  );
}

export default function AISettings({ onClose }) {
  const { settings, saveSettings, removeKey, reloadSettings } = useAIJobs();
  const [mode, setMode] = useState("toons");
  const [provider, setProvider] = useState("claude");
  const [apiKey, setApiKey] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!settings) reloadSettings();
  }, [settings, reloadSettings]);

  useEffect(() => {
    if (!settings) return;
    setMode(settings.mode || "toons");
    if (settings.provider) setProvider(settings.provider);
  }, [settings]);

  if (!settings) return <div className="ais-card ais-settings"><div className="ais-muted">Loading AI settings...</div></div>;

  const p = PROVIDERS[provider];
  const keySaved = settings.hasKey && settings.provider === provider;

  const choose = async (next) => {
    setMessage(null);
    setMode(next);
    // Switching to Toons AI, or back to a key that is already saved, is saved straight away.
    if (next === "toons" || (next === "own" && settings.hasKey)) {
      setBusy(true);
      const res = await saveSettings({ mode: next });
      setBusy(false);
      if (!res.status) setMessage({ type: "error", text: res.message });
    }
  };

  const saveKey = async () => {
    setMessage(null);
    if (!apiKey.trim()) return setMessage({ type: "error", text: "Paste your API key first." });
    setBusy(true);
    const res = await saveSettings({ mode: "own", provider, apiKey: apiKey.trim() });
    setBusy(false);
    if (res.status) {
      setApiKey("");
      setMessage({ type: "ok", text: `Connected. Your ${p.short} key works and is saved securely.` });
    } else setMessage({ type: "error", text: res.message || "The key could not be saved." });
  };

  const forget = async () => {
    setBusy(true);
    const res = await removeKey();
    setBusy(false);
    setMessage(res.status ? { type: "ok", text: "Your key was removed. Using Toons AI." } : { type: "error", text: res.message });
  };

  return (
    <div className="ais-card ais-settings">
      <div className="ais-settings-head">
        <div>
          <div className="ais-h3">AI settings</div>
          <div className="ais-muted">Choose which AI writes your templates and AI content.</div>
        </div>
        {onClose && (
          <button className="ais-icon-btn dark" onClick={onClose} title="Close">
            <span className="material-icons">close</span>
          </button>
        )}
      </div>

      <div className="ais-options">
        <button className={"ais-option" + (mode === "toons" ? " is-on" : "")} onClick={() => choose("toons")} disabled={busy}>
          <span className="ais-radio" />
          <span className="ais-option-body">
            <span className="ais-option-title"><span className="material-icons">auto_awesome</span> Toons AI</span>
            <span className="ais-muted">Included with your plan. Nothing to set up.</span>
            {!settings.toonsAvailable && <span className="ais-warn">Not available yet on this server.</span>}
            {mode === "toons" && settings.toonsAvailable && <CreditsMeter credits={settings.credits} />}
          </span>
        </button>

        <button className={"ais-option" + (mode === "own" ? " is-on" : "")} onClick={() => choose("own")} disabled={busy}>
          <span className="ais-radio" />
          <span className="ais-option-body">
            <span className="ais-option-title"><span className="material-icons">vpn_key</span> My own API key</span>
            <span className="ais-muted">Use your Claude or OpenAI account. No Toons credits are used; your provider bills you.</span>
            {settings.hasKey && (
              <span className="ais-connected">
                <span className="material-icons">verified_user</span> {PROVIDERS[settings.provider] ? PROVIDERS[settings.provider].short : settings.provider} key ending &hellip;{settings.keyHint}
              </span>
            )}
          </span>
        </button>
      </div>

      {mode === "own" && (
        <div className="ais-keybox">
          <div className="ais-seg">
            {Object.keys(PROVIDERS).map((k) => (
              <button key={k} className={provider === k ? "is-on" : ""} onClick={() => { setProvider(k); setMessage(null); }}>
                {PROVIDERS[k].name}
              </button>
            ))}
          </div>
          <label className="ais-label">
            {keySaved ? `Replace your ${p.short} API key` : `Your ${p.short} API key`}
          </label>
          <div className="ais-keyrow">
            <div className="ais-input-wrap">
              <input
                className="ais-input"
                type={show ? "text" : "password"}
                autoComplete="off"
                spellCheck={false}
                placeholder={p.prefix + "..."}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveKey()}
              />
              <button className="ais-eye" onClick={() => setShow(!show)} title={show ? "Hide" : "Show"}>
                <span className="material-icons">{show ? "visibility_off" : "visibility"}</span>
              </button>
            </div>
            <button className="ais-btn primary" onClick={saveKey} disabled={busy}>
              {busy ? <span className="ais-spinner light" /> : <span className="material-icons">flash_on</span>} Test &amp; save
            </button>
          </div>
          <div className="ais-help">
            An API key is not your ChatGPT or Claude login. Create one at{" "}
            <a href={p.url} target="_blank" rel="noopener noreferrer">{p.where}</a> (billed by {p.short}, pay as you go).
          </div>
          {settings.hasKey && (
            <button className="ais-link danger" onClick={forget} disabled={busy}>Remove my key</button>
          )}
        </div>
      )}

      {message && <div className={"ais-msg " + message.type}>{message.text}</div>}
    </div>
  );
}
